import { Checkbox } from '@rio-cloud/rio-uikit';
import { memo, useMemo, useState } from 'react';
import type { IAsset } from '../../../services/memoryAssetService';
import UptimeAssetRow from './UptimeAssetRow';
import { compareAlphaFirstThenNumericNames } from "../../../utils/stringUtils";

import '../styles.scss';

interface UptimeTagRowProps {
    tagId: string;
    tagName: string;
    assets: IAsset[];
    selectedAssetIds: Set<string>;

    onAssetsSelect: (tagAssetIds: string[], selectedAssetIds: string[]) => void;
}

const UptimeTagRow = ({
    tagId,
    tagName,
    assets,
    selectedAssetIds,

    onAssetsSelect,
}: UptimeTagRowProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const tagAssetIds = useMemo(() => assets.map(asset => asset.id), [assets]);

    const areAllTagAssetsSelected =
        tagAssetIds.length > 0 && tagAssetIds.every(assetId => selectedAssetIds.has(assetId));

    const areSomeTagAssetsSelected =
        tagAssetIds.some(assetId => selectedAssetIds.has(assetId)) && !areAllTagAssetsSelected;

    const getTagSelectedAssetIds = () => tagAssetIds.filter(assetId => selectedAssetIds.has(assetId));

    const toggleAssetSelection = (assetId: string) => {
        const tagSelection = new Set(getTagSelectedAssetIds());

        if (tagSelection.has(assetId)) {
            tagSelection.delete(assetId);
        } else {
            tagSelection.add(assetId);
        }

        onAssetsSelect(tagAssetIds, Array.from(tagSelection));
    };

    const toggleAllAssetsSelection = () => {
        const tagSelection = new Set(getTagSelectedAssetIds());
        const shouldDeselectAllTagAssets =
            tagAssetIds.length > 0 && tagAssetIds.every(assetId => tagSelection.has(assetId));

        if (shouldDeselectAllTagAssets) {
            tagAssetIds.forEach(assetId => tagSelection.delete(assetId));
        } else {
            tagAssetIds.forEach(assetId => tagSelection.add(assetId));
        }

        onAssetsSelect(tagAssetIds, Array.from(tagSelection));
    };

    return assets.length > 0 ? (
        <div key={tagId} className={`TreeNodeContainer user-select-none${isExpanded ? ' open' : ''}`}>
            <div className='TreeNode from-group' onClick={() => setIsExpanded(!isExpanded)}>
                <Checkbox
                    className='TreeCheckbox'
                    checked={areAllTagAssetsSelected}
                    indeterminate={areSomeTagAssetsSelected}
                    onChange={e => {
                        e.stopPropagation();
                        toggleAllAssetsSelection();
                    }}
                />
                <div className='TreeLabel'>
                    <div className='TreeLabelName'>
                        <div className='TreeLabelNameText display-flex'>
                            <div className='TreeLabelNameTextHeadline'>{tagName}</div>
                            <div className='TreeLabelCount label margin-left-10 margin-right-10 label-muted label-filled label-condensed'>
                                {assets.length}
                            </div>
                        </div>
                        <div
                            className={`margin-inline-10 rioglyph text-size-12 ${isExpanded ? 'rioglyph-chevron-up' : 'rioglyph-chevron-down'}`}
                        />
                    </div>
                </div>
            </div>

            {isExpanded &&
                assets.map(asset => (
                    <UptimeAssetRow
                        key={asset.id}
                        asset={asset}
                        tagName={tagName}
                        isChecked={selectedAssetIds.has(asset.id)}
                        onAssetSelect={toggleAssetSelection}
                    />
                ))}
        </div>
    ) : null;
};

export default memo(UptimeTagRow);
